import Grid from '@mui/material/Grid';
import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { useStoreActions, useStoreState } from '~/store/hooks';
import { extractErrorMessage } from '~/utils/error/error';
import formatDictionaryOptions from '~/utils/formatDictionaryOptions';
import { normalizeDateStringWithCurrentUtcTime, setNoon } from '~/utils/date/date';
import { IClientTeam, IClientTeamRequest } from '~/services/api/clientDetails/types';
import { IDictionaryTypes } from '~/services/api/dictionaries/types';
import Button from '~/ui/components/common/Button';
import Loader from '~/ui/components/common/Loader';
import Checkbox from '~/ui/components/inputs/Checkbox';
import DatePicker from '~/ui/components/inputs/DatePicker';
import Select from '~/ui/components/inputs/SelectWithoutAnimation';

import { VIEW_CLIENT } from '~/ui/constants/paths';
import { IDetails } from '../types';
import { teamValidationSchema } from './validates';

import { IRequestInfo } from './types';

import variables from '~/ui/assets/styles/colors.module.scss';
import styles from './Styles.module.scss';

interface IProps {
  showSecondSubmit: boolean;
  defaultValues: IClientTeamRequest;
  details: IDetails | null;
  requestInfo: IRequestInfo;
  setModalTitle: (str: string) => void;
  setDetails: (arg: IDetails) => void;
}

const AddTeam = ({
  showSecondSubmit,
  defaultValues,
  details,
  requestInfo,
  setModalTitle,
  setDetails,
}: IProps): ReactElement => {
  const navigate = useNavigate();

  const [processing, setProcessing] = useState(false);
  const [loading, setLoading] = useState(false);

  const {
    control,
    formState: { errors },
    reset,
    handleSubmit,
  } = useForm({
    defaultValues: { ...(defaultValues || { setAsPrimary: false }) },
    resolver: teamValidationSchema,
  });
  const { actTeams = [] } = useStoreState(state => state.clientDetails);

  const { showError, showNotify } = useStoreActions(actions => actions.snackbar);
  const { onUpdateTeam, onCreateTeam, setCurrent } = useStoreActions(
    actions => actions.clientDetails,
  );
  const onGetDictionariesByType = useStoreActions(
    actions => actions.dictionaries.onGetDictionariesByType,
  );
  const { onGetActTeamsAvailable } = useStoreActions(actions => actions.actTeam);
  const { availableActTeams } = useStoreState(state => state.actTeam);
  const { clinic } = useStoreState(state => state.user.current);
  const referralSourceDictionary =
    useStoreState(
      state => state.dictionaries.availableDictionaries[IDictionaryTypes.ReferralSource]?.list,
    ) || [];

  const teamOptions = formatDictionaryOptions(availableActTeams);
  const referralSourceOptions = formatDictionaryOptions(referralSourceDictionary);

  // when superviser (LA,PA) changes client primary team we need to update url with new primary team id for correct future api requests
  const refreshTeam = (teamId: string, clientId: string) => {
    navigate(VIEW_CLIENT.replace(':clientId', clientId).replace(':actTeamId', teamId));
  };
  const onSubmit = async (vals: IClientTeamRequest, evt: React.BaseSyntheticEvent) => {
    const { name } = (evt as React.BaseSyntheticEvent<HTMLFormElement>).nativeEvent.submitter;
    const updatedPrimaryTeam = !defaultValues?.setAsPrimary && vals.setAsPrimary;

    try {
      setProcessing(true);
      const isUpdate = Boolean(details?.id);

      const payload = {
        requestInfo: (isUpdate
          ? { ...requestInfo, id: String(details.id) }
          : requestInfo) as IRequestInfo & { id: string },
        requestPayload: {
          ...vals,
          admissionDate: normalizeDateStringWithCurrentUtcTime(vals.admissionDate),
        },
      };

      const teams: IClientTeam[] = isUpdate
        ? await onUpdateTeam(payload)
        : await onCreateTeam(payload);

      const teamId = teams.find(team => team.isPrimary)?.team.id;

      if (isUpdate) {
        setDetails(null);
        setCurrent(null);
      } else {
        reset({ referralSourceId: null, teamId: null, admissionDate: null });
      }

      setModalTitle(name);
      showNotify({ message: `Team successfully ${isUpdate ? 'update' : 'added'}` });

      if (updatedPrimaryTeam && teamId) {
        refreshTeam(String(teamId), requestInfo.clientId);
      }
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setProcessing(false);
    }
  };

  const onMount = useCallback(async () => {
    try {
      if (!clinic?.id) {
        return;
      }
      setLoading(true);
      onGetDictionariesByType({ type: IDictionaryTypes.ReferralSource });
      onGetActTeamsAvailable({ clinicId: String(clinic.id) });
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [clinic?.id, onGetActTeamsAvailable, onGetDictionariesByType, showError]);

  useEffect(() => {
    onMount();
  }, [onMount]);

  const clientTeamOptions = useMemo(
    () =>
      teamOptions.filter(
        team => !actTeams.some(actTeam => !actTeam.isArchived && actTeam.team.id === team.value),
      ),
    [teamOptions, actTeams],
  );

  if (loading) return <Loader />;

  return (
    <form onSubmit={handleSubmit(onSubmit as SubmitHandler<FieldValues>)}>
      <Grid container spacing={2}>
        {!defaultValues && (
          <Grid
            size={{
              sm: 12,
            }}
          >
            <Select
              name="teamId"
              hideSelectedOptions={false}
              options={clientTeamOptions}
              control={control}
              errors={errors}
              maxMenuHeight={200}
              label="Select Team"
            />
          </Grid>
        )}
        <Grid
          size={{
            sm: 12,
          }}
        >
          <Select
            name="referralSourceId"
            hideSelectedOptions={false}
            options={referralSourceOptions}
            control={control}
            errors={errors}
            maxMenuHeight={200}
            label="Select Referral Source"
          />
        </Grid>
        <Grid
          size={{
            sm: 12,
          }}
        >
          <DatePicker
            control={control}
            openTo="date"
            errors={errors}
            label="Date of Admission"
            name="admissionDate"
            maxDate={setNoon(new Date())}
          />
        </Grid>
        {!defaultValues?.setAsPrimary && (
          <Grid
            size={{
              sm: 12,
            }}
          >
            <Checkbox
              size="small"
              name="setAsPrimary"
              control={control}
              errors={errors}
              label="Primary Team"
            />
          </Grid>
        )}
      </Grid>
      <div className={styles.buttonsWrapper}>
        <Button
          color="primary"
          variant="outlined"
          className={styles.margin}
          onClick={() => {
            setDetails(null);
            setModalTitle(null);
            setCurrent(null);
          }}
        >
          Cancel
        </Button>
        <Button
          color="primary"
          variant="contained"
          type="submit"
          name={null}
          isLoading={processing}
          loaderColor={variables.colorGrey}
          disabled={processing}
          className={!showSecondSubmit ? styles.margin : ''}
        >
          {showSecondSubmit ? 'Save' : 'Update'}
        </Button>
      </div>
    </form>
  );
};

export default AddTeam;
